/**
 * 认证服务
 */

export interface LoginRequest {
    rememberMe: boolean;
    name: string;
    password: string;
    loginBindingParameters: {
        weChatOpenId: string;
    };
    unbindingRedirectUrlType: string;
}

export interface LoginResponse {
    success: boolean;
    message?: string;
    data?: any;
    token?: string;
}

/**
 * 登录API调用
 */
export const login = async (loginData: LoginRequest): Promise<LoginResponse> => {
    try {
        // 使用代理访问后端接口
        const apiUrl = '/api/auth/login';

        // 从URL获取weChatOpenId
        const urlParams = new URLSearchParams(window.location.search);
        const weChatOpenId = urlParams.get('weChatOpenId') || '';

        // 构造后端接口需要的数据格式
        const backendLoginData = {
            name: loginData.name,
            password: loginData.password, // 密码已经在前端加密了
            weChatOpenId: weChatOpenId
        };

        console.log('发送登录请求:', { url: apiUrl, data: backendLoginData });

        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(backendLoginData),
        });

        console.log('登录响应状态:', response.status, response.statusText);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('登录失败响应:', {
                status: response.status,
                statusText: response.statusText,
                headers: Object.fromEntries(response.headers.entries()),
                body: errorText
            });

            if (response.status === 403) {
                throw new Error('访问被拒绝，请检查账号权限或联系管理员');
            } else if (response.status === 401) {
                throw new Error('认证失败，请检查账号和密码');
            } else if (response.status === 404) {
                throw new Error('登录接口不存在，请联系技术支持');
            } else if (response.status >= 500) {
                throw new Error('服务器内部错误，请稍后重试');
            } else {
                throw new Error(`登录失败: ${response.status} ${response.statusText}`);
            }
        }

        const result = await response.json();
        console.log('后端代理响应:', result);

        // 解析 originalResponse 来判断真正的登录结果
        if (result.success && result.originalResponse) {
            try {
                const originalData = JSON.parse(result.originalResponse);
                console.log('原始登录响应:', originalData);

                // 根据原始响应判断登录是否成功
                // 成功状态可能是 'success' 或 'login'，并且需要有token
                if ((originalData.status === 'success' || originalData.status === 'login'
                    || originalData.errorMessage!='用户名或密码不正确') ) {
                    return {
                        success: true,
                        data: originalData,
                    };
                } else {
                    // 登录失败，返回失败结果而不是抛出错误
                    const errorMessage = originalData.errorMessage || '登录失败';
                    return {
                        success: false,
                        message: errorMessage,
                    };
                }
            } catch (parseError) {
                console.error('解析原始响应失败:', parseError);
                return {
                    success: false,
                    message: '服务器响应格式错误',
                };
            }
        } else {
            // 后端代理本身失败
            const errorMessage = result.message || '后端代理请求失败';
            return {
                success: false,
                message: errorMessage,
            };
        }
    } catch (error) {
        console.error('登录请求错误:', error);

        // 如果是CORS错误，提供更明确的提示
        if (error instanceof TypeError && error.message.includes('fetch')) {
            return {
                success: false,
                message: 'CORS跨域错误，请联系技术支持配置服务器允许跨域访问',
            };
        }

        return {
            success: false,
            message: error instanceof Error ? error.message : '网络错误，请检查网络连接后重试',
        };
    }
};

/**
 * 从URL获取微信OpenId
 */
export const getWeChatOpenIdFromUrl = (): string => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('weChatOpenId') || urlParams.get('openid') || '';
};

/**
 * 构建登录请求数据
 */
export const buildLoginRequest = (
    username: string,
    encryptedPassword: string,
    rememberMe: boolean = true,
    weChatOpenId?: string
): LoginRequest => {
    const openId = weChatOpenId || getWeChatOpenIdFromUrl();

    return {
        rememberMe,
        name: username,
        password: encryptedPassword,
        loginBindingParameters: {
            weChatOpenId: openId,
        },
        unbindingRedirectUrlType: 'WE_CHAT',
    };
};
